/**
 * Keeping the recording a stranger made on bloxx.video.
 *
 * THE HANDOFF CANNOT USE STORAGE. The homepage is bloxx.video and this app is
 * app.itsbloxx.com, so sessionStorage, localStorage and IndexedDB are all a
 * different origin's and unreadable here. The recording's id and its claim
 * token travel in the URL FRAGMENT instead (`/studio#keep=<id>.<token>`), which
 * the browser never puts in a request, so the token stays out of access logs,
 * out of the Referer header and off every server between the two hosts.
 *
 * IT IS CAPTURED BEFORE ROUTING, from main.tsx, because RequireAuth sends a
 * signed-out visitor to /login carrying only `location.pathname` and the
 * fragment is dropped on the way. So the fragment is read once, moved into this
 * origin's sessionStorage, and stripped from the address bar so a shared or
 * bookmarked URL does not carry a credential around.
 *
 * It is a one-shot: `takeKeep` removes it as it reads it, so a failed claim
 * cannot loop, and a recording already claimed is not claimed twice.
 */
const KEY = "bloxx:studio:keep";

export interface KeepHandoff {
  recording: string;
  token: string;
}

const parse = (raw: string): KeepHandoff | null => {
  // `<id>.<token>`: both are hex from the Worker, so anything else is noise.
  const m = /^([0-9a-f-]{16,64})\.([0-9a-f]{16,128})$/i.exec(raw.trim());
  return m ? { recording: m[1]!, token: m[2]! } : null;
};

/** Read `#keep=…` into this origin's storage, then take it out of the address bar. */
export function captureKeep(): void {
  const hash = window.location.hash;
  if (!hash.includes("keep=")) return;
  const found = new URLSearchParams(hash.replace(/^#/, "")).get("keep");
  const handoff = found && parse(found);
  if (handoff) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(handoff));
    } catch {
      // A private window with storage off: the claim is lost, and the visitor
      // still has the link itself, which works for its 24 hours.
    }
  }
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, "", `${pathname}${search}`);
}

/** The pending handoff, removed as it is read. */
export function takeKeep(): KeepHandoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as KeepHandoff;
    return parsed && typeof parsed.recording === "string" && typeof parsed.token === "string" ? parsed : null;
  } catch {
    return null;
  }
}
